import {ICartPosition, IMoney} from "@connox/typescript-library";
import {ICartMessage} from "@connox/typescript-library/types/cart/CartMessage";
import {ICartRebateVoucher} from "@connox/typescript-library/types/cart/CartRebateVoucher";
import * as React from "react";
import {useRef, useState} from "react";
import {Gateway} from "react-gateway";
import {useTranslation} from "react-i18next";
import {FaCheck} from "react-icons/fa";
import {useMediaQuery} from "react-responsive";
import {useCustomerContext} from "../../customer/CustomerContextProvider/CustomerContextProvider";
import ApplicableVouchersModal from "../../lib/ModalWindow/ApplicableVouchersModal";
import {isCart, useCartContext, useUnsafeCartContext} from "../CartContextProvider/CartContextProvider";
import "./VoucherBlock.scss";
import * as Sentry from "@sentry/browser";
import {IPercent} from "@connox/typescript-library/types/cart/Percent";
import {VoucherType} from "@connox/typescript-library/types/cart/VoucherType";

const VoucherBlock: React.FC = () => {
  const context = useUnsafeCartContext();
  const {t} = useTranslation("cart");
  const voucherInputRef = useRef<HTMLInputElement>(null);

  if (!context) {
    return null;
  }

  const CheckIcon = () => (
    <span onClick={submitVoucherCode}>
      <FaCheck color={"#d3d3d3"} />
    </span>
  );

  const submitVoucherCode = async (evt: React.MouseEvent<HTMLSpanElement> | React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    if (!voucherInputRef.current) {
      return;
    }

    const voucherCode = voucherInputRef.current.value;

    if (!isCart(context)) {
      // todo finisch this
      context.requestCart().then(({cart, done}) => {
        if (voucherCode !== "") {
          cart.addVoucher(voucherCode).then(() => {
            if (!voucherInputRef.current) {
              return;
            }

            voucherInputRef.current.value = "";
          }, Sentry.captureException);
        }

        done();
      }, Sentry.captureException);
    }

    if (isCart(context) && voucherCode !== "") {
      await context.cart.addVoucher(voucherCode);
      voucherInputRef.current.value = "";
    }
  };

  return (
    <div className="Voucher">
      <form onSubmit={submitVoucherCode}>
        <input type="text" name="VoucherInput" ref={voucherInputRef} placeholder={t("Gutschein einlösen?")} />
        <CheckIcon />
      </form>
      {isCart(context) && <VoucherList />}
    </div>
  );
};

const EmptyVoucher: React.FC<{position: ICartPosition}> = (props) => {
  if (!props.position.reference) {
    return null;
  }
  const {voucherCode} = props.position.reference;
  const {messages} = props.position;
  const {text} = props.position;

  return (
    <VoucherLine
      code={voucherCode || ""}
      messages={messages}
      minimumOrderValueAbsolute={null}
      text={text}
      voucherAmount={null}
      voucherPercentage={null}
      type={null}
    />
  );
};

const VoucherSwitch: React.FC<{position: ICartPosition}> = (props) => {
  const {reference, text} = props.position;

  if (reference?.voucher) {
    return <AddApplicabilityMessages voucher={reference.voucher} text={text} />;
  }

  return <EmptyVoucher position={props.position} />;
};

/*
 * whole voucher list
 */
const VoucherList: React.FC = () => {
  const {cart} = useCartContext();
  const voucherLines = cart.positions.filter<ICartPosition>(
    (pos: ICartPosition): pos is ICartPosition => pos.type === "Voucher",
  );
  const {rebates} = cart;

  return (
    <div className="Voucher__List">
      {rebates.map((rebate) => (
        <VoucherLine key={rebate.code} {...rebate} text={rebate.code} />
      ))}
      {voucherLines.map((voucherLine, i) => (
        <VoucherSwitch key={voucherLine.reference?.voucherCode || `voucher_switch_${i}`} position={voucherLine} />
      ))}
    </div>
  );
};

/*
 * fill applicability messages to cart position type voucher
 */
const AddApplicabilityMessages: React.FC<{text: string; voucher: ICartRebateVoucher}> = (props: {
  text: string;
  voucher: ICartRebateVoucher;
}) => {
  const {cart} = useCartContext();
  const {text, voucher} = props;
  const {messages, type} = voucher;
  const applicabilityMessages: ICartMessage[] = [];

  const cartItemList = cart.positions.filter<ICartPosition>(
    (pos): pos is ICartPosition => !!pos.reference?.itemId && !!pos.item,
  );
  const relativeVoucherApplicableItems = cartItemList.filter<ICartPosition>(
    (pos): pos is ICartPosition => pos.discountability.isRelativeVoucherApplicable,
  );
  const fixedVoucherApplicableItems = cartItemList.filter<ICartPosition>(
    (pos): pos is ICartPosition => pos.discountability.isFixedVoucherApplicable,
  );

  if (type === "Fix") {
    // fixed voucher applicability
    if (!fixedVoucherApplicableItems.length) {
      // not any applicable item found
      applicabilityMessages.push({code: NO_VOUCHER_APPLICABLE_ITEM_FOUND, field: "", message: "", type: "error"});
    } else if (cartItemList.length !== fixedVoucherApplicableItems.length) {
      // some, but not every item applicable
      applicabilityMessages.push({code: NOT_ALL_ITEMS_VOUCHER_APPLICABLE, field: "", message: "", type: "warning"});
    }
    // else, all items are applicable (no message added)
  } else if (type === "Relative") {
    // relative voucher applicability
    if (!relativeVoucherApplicableItems.length) {
      // not any applicable item found
      applicabilityMessages.push({code: NO_VOUCHER_APPLICABLE_ITEM_FOUND, field: "", message: "", type: "error"});
    } else if (cartItemList.length !== relativeVoucherApplicableItems.length) {
      // some, but not every item applicable
      applicabilityMessages.push({code: NOT_ALL_ITEMS_VOUCHER_APPLICABLE, field: "", message: "", type: "warning"});
    }
    // else, all items are applicable (no message added)
  }
  const allMessages = [...messages, ...applicabilityMessages];

  return <VoucherLine {...voucher} messages={allMessages} text={text} />;
};

const VoucherLine: React.FC<{
  code: string;
  messages: ICartMessage[];
  minimumOrderValueAbsolute: IMoney | null;
  text: string;
  type: VoucherType | null;
  voucherAmount: IMoney | null;
  voucherPercentage: IPercent | null;
}> = (props) => {
  const {cart} = useCartContext();
  const {code, messages, minimumOrderValueAbsolute, text, type, voucherAmount, voucherPercentage} = props;

  const voucherValue = type === "Fix" ? voucherAmount : voucherPercentage;
  const voucherName = voucherValue?.formatted ? `${voucherValue.formatted} (${text})` : text;

  return (
    <div key={`voucher_${code}`} className="Voucher__List__row">
      <div className="Voucher__List__row--title">
        <div className="Voucher-Icon">
          <span
            className="Svg Svg-Icon-Close Voucher__List__row--icon"
            onClick={async () => {
              await cart.removeVoucher(code);
            }}
            style={{cursor: "pointer"}}
          >
            &#160;
          </span>
        </div>
        <div className="Voucher-Name">
          <strong>{voucherName}</strong>
        </div>
      </div>
      {messages.map((msg) => (
        <VoucherItemMessage
          key={`${code}_${msg.code}`}
          currentVoucherCode={code}
          minimumOrderValue={minimumOrderValueAbsolute?.formatted || ""}
          msg={msg}
          voucherTitle={text}
        />
      ))}
    </div>
  );
};

const VoucherItemMessage: React.FC<{
  currentVoucherCode?: string;
  minimumOrderValue: string;
  msg: ICartMessage;
  voucherTitle: string;
}> = (prop) => {
  const {currentVoucherCode, minimumOrderValue, msg, voucherTitle} = prop;
  const {t} = useTranslation("cart");
  // this could have been passed down but lets be quick
  const {customer} = useCustomerContext();
  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const message = t(msg.code, {
    customerId: customer.customerId,
    minimumOrderValue,
    voucherCode: currentVoucherCode,
  });
  const textDivRef = useRef<HTMLDivElement>(null);
  const isRealError = !(
    msg.code === VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED || msg.code === NOT_ALL_ITEMS_VOUCHER_APPLICABLE
  );
  const deviceString = `Button${useMediaQuery({query: "(min-width: 768px)"}) ? "Desktop" : "Mobile"}`;

  const WhyNotLink: React.FC = () =>
    msg.code === "NOT_ALL_ITEMS_VOUCHER_APPLICABLE" || msg.code === "NO_VOUCHER_APPLICABLE_ITEM_FOUND" ? (
      <React.Fragment>
        {". "}
        <a
          href="#"
          onClick={(evt: React.MouseEvent<HTMLAnchorElement>) => {
            evt.preventDefault();
            setPopupVisible(true);
          }}
        >
          {t("Warum nicht?")}
        </a>
      </React.Fragment>
    ) : null;

  return (
    <div key={msg.code} ref={textDivRef} className="Voucher__List__row--text">
      <div className="Voucher-Caution">
        <span className={`Svg Svg-Icon-Caution${isRealError ? "" : "-Black"} Voucher__List__row--icon`}>&#160;</span>
      </div>
      <div className="Voucher-Warning">
        <ApplicableVouchersModal
          closeFunction={() => setPopupVisible(false)}
          isVisible={popupVisible}
          voucherTitle={voucherTitle}
        />
        <p>
          {message}
          <WhyNotLink />
        </p>
        {isRealError && <Gateway into={deviceString}>{msg.code}</Gateway>}
      </div>
    </div>
  );
};

/**
 * @component
 */
export default VoucherBlock;

const NO_VOUCHER_APPLICABLE_ITEM_FOUND = "NO_VOUCHER_APPLICABLE_ITEM_FOUND";
const NOT_ALL_ITEMS_VOUCHER_APPLICABLE = "NOT_ALL_ITEMS_VOUCHER_APPLICABLE";
const VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED = "VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED";
